import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ChevronLeft, Users, Calendar, ShieldCheck, Search, 
  Trash2, UserCheck, UserX, BarChart3, DollarSign, TrendingUp,
  Layers, Plus, GripVertical, Eye, EyeOff, Pencil, X, Check, Star,
  Download
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPanel = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [tab, setTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [activitiesTotal, setActivitiesTotal] = useState(0);
  const [searchUsers, setSearchUsers] = useState('');
  const [searchActivities, setSearchActivities] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [themes, setThemes] = useState([]);
  const [editingTheme, setEditingTheme] = useState(null);
  const [newTheme, setNewTheme] = useState(null);
  const [demoCount, setDemoCount] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/stats`, { headers });
      setStats(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Acceso denegado');
        navigate('/prelanzamiento');
      }
    }
  }, [token]);

  const fetchRevenue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/revenue`, { headers });
      setRevenue(res.data);
    } catch (e) { console.error("non-critical fetch error:", e); }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({ skip: '0', limit: '50' });
      if (roleFilter) params.append('role', roleFilter);
      if (searchUsers) params.append('search', searchUsers);
      const res = await axios.get(`${API}/api/admin/users?${params}`, { headers });
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
    } catch (e) { console.error("non-critical fetch error:", e); }
  }, [token, roleFilter, searchUsers]);

  const fetchActivities = useCallback(async () => {
    try {
      const params = new URLSearchParams({ skip: '0', limit: '50' });
      if (searchActivities) params.append('search', searchActivities);
      const res = await axios.get(`${API}/api/admin/activities?${params}`, { headers });
      setActivities(res.data.activities);
      setActivitiesTotal(res.data.total);
    } catch (e) { console.error("non-critical fetch error:", e); }
  }, [token, searchActivities]);

  const availableCategories = [
    'bienestar', 'causas', 'charlas', 'comida', 'creativo', 'cultura', 'innovacion', 'naturaleza'
  ];

  const fetchThemes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/recommendation-themes`, { headers });
      setThemes(res.data.themes || []);
    } catch (e) { console.error("non-critical fetch error:", e); }
  }, [token]);

  const fetchDemoStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/demo-activities`, { headers });
      setDemoCount(res.data.count);
    } catch (e) { console.error("non-critical fetch error:", e); }
  }, [token]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchStats(), fetchRevenue()]).finally(() => setLoading(false));
  }, [fetchStats, fetchRevenue]);

  useEffect(() => { if (tab === 'users') fetchUsers(); }, [tab, fetchUsers]);
  useEffect(() => { if (tab === 'activities') { fetchActivities(); fetchDemoStatus(); } }, [tab, fetchActivities, fetchDemoStatus]);
  useEffect(() => { if (tab === 'themes') fetchThemes(); }, [tab, fetchThemes]);

  const handleSeedDemo = async () => {
    setDemoBusy(true);
    try {
      const res = await axios.post(`${API}/api/admin/demo-activities`, {}, { headers });
      toast.success(`${res.data.count} actividades de ejemplo listas`);
      setDemoCount(res.data.count);
      fetchActivities();
      fetchStats();
    } catch (e) {
      toast.error('Error al generar actividades de ejemplo');
    } finally {
      setDemoBusy(false);
    }
  };

  const handleDeleteDemo = async () => {
    if (!window.confirm('¿Eliminar todas las actividades de ejemplo? Esta acción se puede revertir generándolas de nuevo.')) return;
    setDemoBusy(true);
    try {
      const res = await axios.delete(`${API}/api/admin/demo-activities`, { headers });
      toast.success(`${res.data.deleted} actividades de ejemplo eliminadas`);
      setDemoCount(0);
      fetchActivities();
      fetchStats();
    } catch (e) {
      toast.error('Error al eliminar actividades de ejemplo');
    } finally {
      setDemoBusy(false);
    }
  };


  const handleToggleRole = async (userId, currentRole) => {
    const newRole = currentRole === 'creator' ? 'user' : 'creator';
    try {
      await axios.put(`${API}/api/admin/users/${userId}`, { role: newRole }, { headers });
      toast.success(`Rol cambiado a ${newRole}`);
      fetchUsers();
      fetchStats();
    } catch (e) {
      toast.error('Error al actualizar');
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`¿Eliminar a ${name}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/api/admin/users/${userId}`, { headers });
      toast.success('Usuario eliminado');
      fetchUsers();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleDeleteActivity = async (activityId, title) => {
    if (!window.confirm(`¿Eliminar "${title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/api/admin/activities/${activityId}`, { headers });
      toast.success('Actividad eliminada');
      fetchActivities();
      fetchStats();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const handleCreateTheme = async () => {
    if (!newTheme?.title || !newTheme?.categories?.length) {
      toast.error('Título y al menos una categoría son requeridos');
      return;
    }
    try {
      await axios.post(`${API}/api/admin/recommendation-themes`, {
        title: newTheme.title,
        subtitle: newTheme.subtitle || '',
        categories: newTheme.categories,
        order: themes.length + 1,
        is_active: true
      }, { headers });
      toast.success('Tema creado');
      setNewTheme(null);
      fetchThemes();
    } catch (e) {
      toast.error('Error al crear tema');
    }
  };

  const handleUpdateTheme = async (themeId, data) => {
    try {
      await axios.put(`${API}/api/admin/recommendation-themes/${themeId}`, data, { headers });
      toast.success('Tema actualizado');
      setEditingTheme(null);
      fetchThemes();
    } catch (e) {
      toast.error('Error al actualizar');
    }
  };

  const handleDeleteTheme = async (themeId, title) => {
    if (!window.confirm(`¿Eliminar tema "${title}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/recommendation-themes/${themeId}`, { headers });
      toast.success('Tema eliminado');
      fetchThemes();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  };

  const handleToggleTheme = async (themeId, isActive) => {
    await handleUpdateTheme(themeId, { is_active: !isActive });
  };

  const handleToggleFeatured = async (activityId) => {
    try {
      const res = await axios.put(`${API}/api/admin/activities/${activityId}/featured`, {}, { headers });
      const isFeatured = res.data.is_featured;
      toast.success(isFeatured ? 'Destacada en carrusel' : 'Removida del carrusel');
      fetchActivities();
    } catch (e) {
      toast.error('Error al actualizar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32 md:pb-8">
      {/* Header - only show when not embedded */}
      {!embedded && (
        <div className="sticky top-14 md:top-11 z-30 bg-white border-b border-gray-100 safe-top">
          <div className="max-w-5xl mx-auto flex items-center justify-between px-4 md:px-6 py-3">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-gray-100">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <h1 className="font-rubik text-lg font-bold text-gray-600">{t('admin.title')}</h1>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 md:px-6 pt-6">
        {/* Tab navigation */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-1 bg-gray-100 p-1 rounded-xl mb-6">
          {[
            { key: 'stats', label: t('admin.summary'), icon: BarChart3 },
            { key: 'revenue', label: t('admin.revenue'), icon: DollarSign },
            { key: 'users', label: t('admin.users'), icon: Users },
            { key: 'activities', label: t('admin.activities'), icon: Calendar },
            { key: 'themes', label: 'Temas', icon: Layers },
            { key: 'export', label: 'Exportar', icon: Download }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-medium transition-all ${
                tab === key ? 'bg-white shadow-sm text-gray-700' : 'text-gray-500 hover:text-gray-600'
              }`}
              data-testid={`admin-tab-${key}`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Stats Tab */}
        {tab === 'stats' && stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="admin-stats">
            {[
              { label: t('admin.users'), value: stats.total_users, color: 'bg-blue-50 text-blue-700' },
              { label: t('admin.creators'), value: stats.total_creators, color: 'bg-purple-50 text-purple-700' },
              { label: t('admin.activities'), value: stats.total_activities, color: 'bg-green-50 text-green-700' },
              { label: t('admin.registrations'), value: stats.total_registrations, color: 'bg-amber-50 text-amber-700' },
              { label: t('dashboard.pendingPayments'), value: stats.pending_payments, color: 'bg-orange-50 text-orange-700' },
              { label: t('dashboard.confirmedPayments'), value: stats.confirmed_payments, color: 'bg-emerald-50 text-emerald-700' }
            ].map(({ label, value, color }) => (
              <div key={label} className={`p-5 rounded-xl ${color.split(' ')[0]}`}>
                <p className="text-xs font-medium opacity-70 mb-1">{label}</p>
                <p className={`text-2xl font-bold ${color.split(' ')[1]}`}>{value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Revenue Tab */}
        {tab === 'revenue' && revenue && (
          <div className="space-y-6" data-testid="admin-revenue">
            {/* Revenue summary cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-5 rounded-xl bg-emerald-50">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  <p className="text-xs font-medium text-emerald-600">{t('admin.confirmedRevenue')}</p>
                </div>
                <p className="text-2xl font-bold text-emerald-700">${(revenue.total_revenue || 0).toLocaleString()} MXN</p>
                <p className="text-xs text-emerald-600 mt-1">{revenue.total_paid_registrations || 0} {t('admin.payments')}</p>
              </div>
              <div className="p-5 rounded-xl bg-amber-50">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-amber-600" />
                  <p className="text-xs font-medium text-amber-600">{t('admin.pendingRevenue')}</p>
                </div>
                <p className="text-2xl font-bold text-amber-700">${(revenue.pending_revenue || 0).toLocaleString()} MXN</p>
                <p className="text-xs text-amber-600 mt-1">{revenue.pending_count || 0} {t('admin.payments')}</p>
              </div>
            </div>

            {/* Revenue by creator */}
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-rubik font-semibold text-sm text-gray-700">{t('admin.revenueByCreator')}</h3>
              </div>
              {(revenue.by_creator || []).length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-sm">{t('admin.noRevenueData')}</div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {(revenue.by_creator || []).map((c, idx) => (
                    <div key={c.creator_id} className="flex items-center justify-between p-4" data-testid={`revenue-creator-${idx}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold">
                          {c.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">{c.name}</p>
                          <p className="text-xs text-gray-400">{c.count} {t('admin.payments')}</p>
                        </div>
                      </div>
                      <p className="font-semibold text-sm text-emerald-700">${c.total.toLocaleString()} MXN</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Revenue by month */}
            {(revenue.by_month || []).length > 0 && (
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="p-4 border-b border-gray-100">
                  <h3 className="font-rubik font-semibold text-sm text-gray-700">{t('admin.revenueByMonth')}</h3>
                </div>
                <div className="divide-y divide-gray-50">
                  {(revenue.by_month || []).map((m) => (
                    <div key={m.month} className="flex items-center justify-between p-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">{m.month}</p>
                        <p className="text-xs text-gray-400">{m.count} {t('admin.payments')}</p>
                      </div>
                      <p className="font-semibold text-sm text-emerald-700">${m.total.toLocaleString()} MXN</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div data-testid="admin-users">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Input
                  value={searchUsers}
                  onChange={(e) => setSearchUsers(e.target.value)}
                  placeholder={t('admin.searchUsers')}
                  className="pr-10 input-airbnb"
                  data-testid="admin-search-users"
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              <div className="flex gap-2">
                {['', 'user', 'creator'].map(r => (
                  <button
                    key={r}
                    onClick={() => setRoleFilter(r)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      roleFilter === r ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`admin-filter-${r || 'all'}`}
                  >
                    {r === '' ? t('admin.all') : r === 'user' ? t('admin.users') : t('admin.creators')}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-3">{usersTotal} usuario(s) encontrados</p>
            <div className="space-y-2">
              {users.map(u => (
                <div key={u.user_id} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-4" data-testid={`admin-user-${u.user_id}`}>
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    <AvatarImage src={u.profile_image} />
                    <AvatarFallback className="bg-primary/10 text-primary text-sm">
                      {u.name?.charAt(0)?.toUpperCase() || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-gray-700 truncate">{u.name}</p>
                    <p className="text-xs text-gray-400 truncate">{u.email}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[10px] font-medium ${
                    u.role === 'creator' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {u.role}
                  </span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleToggleRole(u.user_id, u.role)}
                      className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                      title={u.role === 'creator' ? 'Cambiar a usuario' : 'Cambiar a creador'}
                    >
                      {u.role === 'creator' ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDeleteUser(u.user_id, u.name)}
                      className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"
                      title="Eliminar usuario"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activities Tab */}
        {tab === 'activities' && (
          <div data-testid="admin-activities">
            {/* Demo / sample activities manager */}
            <div className="bg-gradient-to-br from-violet-50 to-sky-50 rounded-xl p-4 border border-violet-100 mb-5" data-testid="admin-demo-activities">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-gray-700">Actividades de ejemplo</p>
                  <p className="text-xs text-gray-500 mt-0.5 max-w-md">
                    24 actividades demo (3 por categoría) a nombre de la cuenta Admin, con fecha al límite máximo (+60 días).
                    {demoCount !== null && (
                      <span className="font-medium text-gray-600"> Actualmente: {demoCount} activas.</span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSeedDemo}
                    disabled={demoBusy}
                    className="btn-primary text-xs h-9"
                    data-testid="demo-seed-button"
                  >
                    {demoCount ? 'Actualizar fechas' : 'Generar ejemplos'}
                  </Button>
                  {!!demoCount && (
                    <Button
                      onClick={handleDeleteDemo}
                      disabled={demoBusy}
                      variant="outline"
                      className="text-xs h-9 text-red-500 border-red-200 hover:bg-red-50"
                      data-testid="demo-delete-button"
                    >
                      Eliminar
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="relative mb-4">
              <Input
                value={searchActivities}
                onChange={(e) => setSearchActivities(e.target.value)}
                placeholder={t('admin.searchActivities')}
                className="pr-10 input-airbnb"
                data-testid="admin-search-activities"
              />
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            <p className="text-xs text-gray-400 mb-3">{activitiesTotal} actividad(es) encontradas</p>
            <div className="space-y-2">
              {activities.map(a => (
                <div key={a.activity_id} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-4" data-testid={`admin-activity-${a.activity_id}`}>
                  <div className="w-14 h-14 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                    {a.cover_image && (
                      <img src={a.cover_image} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="font-medium text-sm text-gray-700 truncate">{a.title}</p>
                      {a.is_featured && (
                        <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 font-medium">Carrusel</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 truncate">
                      {a.creator_name} · {a.price > 0 ? `$${a.price} MXN` : 'Gratis'} · {a.registered_count || 0}/{a.capacity} inscritos
                    </p>
                  </div>
                  <button
                    onClick={() => handleToggleFeatured(a.activity_id)}
                    className={`p-2 rounded-lg flex-shrink-0 transition-colors ${a.is_featured ? 'bg-amber-50 text-amber-500' : 'hover:bg-gray-100 text-gray-300'}`}
                    title={a.is_featured ? 'Quitar del carrusel' : 'Destacar en carrusel'}
                    data-testid={`featured-toggle-${a.activity_id}`}
                  >
                    <Star className={`w-4 h-4 ${a.is_featured ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    onClick={() => handleDeleteActivity(a.activity_id, a.title)}
                    className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 flex-shrink-0"
                    title="Eliminar actividad"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Themes Tab */}
        {tab === 'themes' && (
          <div className="space-y-4" data-testid="admin-themes">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-700">Temas de recomendación</h2>
                <p className="text-sm text-gray-400">Define las filas temáticas que aparecen en el Home</p>
              </div>
              <Button
                onClick={() => setNewTheme({ title: '', subtitle: '', categories: [] })}
                className="bg-primary hover:bg-primary-hover rounded-xl"
                data-testid="add-theme-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo tema
              </Button>
            </div>

            {/* New theme form */}
            {newTheme && (
              <div className="bg-white rounded-xl border-2 border-primary/30 p-5 space-y-4" data-testid="new-theme-form">
                <Input
                  value={newTheme.title}
                  onChange={e => setNewTheme({ ...newTheme, title: e.target.value })}
                  placeholder="Título del tema (ej: Actividades con amigos)"
                  className="font-semibold"
                  data-testid="new-theme-title"
                />
                <Input
                  value={newTheme.subtitle}
                  onChange={e => setNewTheme({ ...newTheme, subtitle: e.target.value })}
                  placeholder="Subtítulo (ej: Planes perfectos para compartir)"
                  data-testid="new-theme-subtitle"
                />
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-2">Categorías vinculadas</p>
                  <div className="flex flex-wrap gap-2">
                    {availableCategories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => {
                          const cats = newTheme.categories.includes(cat)
                            ? newTheme.categories.filter(c => c !== cat)
                            : [...newTheme.categories, cat];
                          setNewTheme({ ...newTheme, categories: cats });
                        }}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                          newTheme.categories.includes(cat)
                            ? 'bg-primary text-white'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                        data-testid={`new-theme-cat-${cat}`}
                      >
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleCreateTheme} className="bg-primary hover:bg-primary-hover rounded-lg" data-testid="save-new-theme">
                    <Check className="w-4 h-4 mr-1" /> Crear
                  </Button>
                  <Button variant="outline" onClick={() => setNewTheme(null)} className="rounded-lg" data-testid="cancel-new-theme">
                    <X className="w-4 h-4 mr-1" /> Cancelar
                  </Button>
                </div>
              </div>
            )}

            {/* Theme list */}
            <div className="space-y-3">
              {themes.map((theme) => (
                <div key={theme.theme_id} className={`bg-white rounded-xl border p-4 transition-all ${theme.is_active ? 'border-gray-100' : 'border-gray-200 opacity-60'}`} data-testid={`admin-theme-${theme.theme_id}`}>
                  {editingTheme === theme.theme_id ? (
                    /* Editing mode */
                    <div className="space-y-3">
                      <Input
                        defaultValue={theme.title}
                        id={`edit-title-${theme.theme_id}`}
                        placeholder="Título"
                        className="font-semibold"
                      />
                      <Input
                        defaultValue={theme.subtitle}
                        id={`edit-subtitle-${theme.theme_id}`}
                        placeholder="Subtítulo"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-2">Categorías</p>
                        <div className="flex flex-wrap gap-2" id={`edit-cats-${theme.theme_id}`}>
                          {availableCategories.map(cat => (
                            <button
                              key={cat}
                              data-cat={cat}
                              data-selected={theme.categories?.includes(cat) ? 'true' : 'false'}
                              onClick={(e) => {
                                const btn = e.currentTarget;
                                const isSelected = btn.dataset.selected === 'true';
                                btn.dataset.selected = isSelected ? 'false' : 'true';
                                btn.className = !isSelected
                                  ? 'px-3 py-1.5 rounded-full text-xs font-medium transition-all bg-primary text-white'
                                  : 'px-3 py-1.5 rounded-full text-xs font-medium transition-all bg-gray-100 text-gray-500 hover:bg-gray-200';
                              }}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                theme.categories?.includes(cat)
                                  ? 'bg-primary text-white'
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                            >
                              {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => {
                            const title = document.getElementById(`edit-title-${theme.theme_id}`).value;
                            const subtitle = document.getElementById(`edit-subtitle-${theme.theme_id}`).value;
                            const catBtns = document.querySelectorAll(`#edit-cats-${theme.theme_id} button[data-selected="true"]`);
                            const categories = Array.from(catBtns).map(b => b.dataset.cat);
                            handleUpdateTheme(theme.theme_id, { title, subtitle, categories });
                          }}
                          className="bg-primary hover:bg-primary-hover rounded-lg"
                          size="sm"
                        >
                          <Check className="w-3 h-3 mr-1" /> Guardar
                        </Button>
                        <Button variant="outline" onClick={() => setEditingTheme(null)} size="sm" className="rounded-lg">
                          <X className="w-3 h-3 mr-1" /> Cancelar
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Display mode */
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-700 text-sm">{theme.title}</h4>
                          {!theme.is_active && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">Oculto</span>
                          )}
                        </div>
                        {theme.subtitle && (
                          <p className="text-xs text-gray-400 mb-2">{theme.subtitle}</p>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {theme.categories?.map(cat => (
                            <span key={cat} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[11px]">
                              {cat}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleToggleTheme(theme.theme_id, theme.is_active)}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"
                          title={theme.is_active ? 'Ocultar' : 'Mostrar'}
                        >
                          {theme.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => setEditingTheme(theme.theme_id)}
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-400"
                          title="Editar"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTheme(theme.theme_id, theme.title)}
                          className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {themes.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <Layers className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                  <p className="text-sm">No hay temas configurados</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Export Tab */}
        {tab === 'export' && (
          <div className="space-y-4">
            <h3 className="font-rubik font-semibold text-lg">Exportar datos</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-1">Lista de espera (Landing)</h4>
                <p className="text-sm text-gray-400 mb-4">Emails registrados en la waitlist de prelanzamiento</p>
                <Button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = `${API}/api/admin/export/waitlist`;
                    link.setAttribute('download', 'utentik_waitlist.csv');
                    // Add auth header via fetch
                    fetch(`${API}/api/admin/export/waitlist`, { headers })
                      .then(r => r.blob())
                      .then(blob => {
                        const url = URL.createObjectURL(blob);
                        link.href = url;
                        link.click();
                        URL.revokeObjectURL(url);
                        toast.success('Waitlist exportada');
                      })
                      .catch(() => toast.error('Error al exportar'));
                  }}
                  className="w-full rounded-xl"
                  data-testid="export-waitlist-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar CSV
                </Button>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center mb-3">
                  <UserCheck className="w-5 h-5 text-amber-600" />
                </div>
                <h4 className="font-semibold text-gray-800 mb-1">Usuarios y creadores</h4>
                <p className="text-sm text-gray-400 mb-4">Todos los usuarios registrados en la plataforma</p>
                <Button
                  onClick={() => {
                    fetch(`${API}/api/admin/export/users`, { headers })
                      .then(r => r.blob())
                      .then(blob => {
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'utentik_usuarios.csv');
                        link.click();
                        URL.revokeObjectURL(url);
                        toast.success('Usuarios exportados');
                      })
                      .catch(() => toast.error('Error al exportar'));
                  }}
                  className="w-full rounded-xl"
                  data-testid="export-users-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar CSV
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
